import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationApi, Notification } from '../services/api/notificationApi';

interface UseNotificationsOptions {
  pageSize?: number;
}

/** How often to poll for new notifications (ms) */
const POLL_INTERVAL_MS = 30000;

/**
 * Custom hook for managing notifications with server-side pagination.
 * Latest notifications come first; call `loadMore` to fetch the next page.
 */
export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const { pageSize = 50 } = options;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  // Kept in a ref so the poller can re-fetch every page the user has loaded
  // without re-creating (and restarting) the interval on every list change.
  const loadedCountRef = useRef(0);
  const [total, setTotal] = useState<number>(0);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch first page of notifications
   */
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const { items, total } = await notificationApi.getNotifications(pageSizeRef.current, 0);
      setNotifications(items);
      setTotal(total);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load the next page of notifications and append to the list
   */
  const loadMore = useCallback(async () => {
    try {
      setLoadingMore(true);
      const offset = notifications.length;
      const { items, total } = await notificationApi.getNotifications(pageSizeRef.current, offset);
      setNotifications(prev => {
        // De-dupe in case of overlap from concurrent inserts
        const seen = new Set(prev.map(n => n.id));
        const merged = [...prev];
        for (const item of items) {
          if (!seen.has(item.id)) merged.push(item);
        }
        return merged;
      });
      setTotal(total);
    } catch (err: any) {
      console.error('Failed to load more notifications:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [notifications.length]);

  /**
   * Silently re-fetch the already-loaded notifications plus the unread count.
   * Unlike `refresh` this never flips `loading`, so the poller can keep the
   * list current without flashing skeletons over an open panel.
   */
  const syncLatest = useCallback(async () => {
    try {
      const limit = Math.max(pageSizeRef.current, loadedCountRef.current);
      const [page, count] = await Promise.all([
        notificationApi.getNotifications(limit, 0),
        notificationApi.getUnreadCount(),
      ]);
      setNotifications(page.items);
      setTotal(page.total);
      setUnreadCount(count);
      setError(null);
    } catch (err: any) {
      console.error('Failed to sync notifications:', err);
    }
  }, []);

  /**
   * Fetch unread count
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await notificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err: any) {
      console.error('Failed to fetch unread count:', err);
    }
  }, []);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationApi.markAsRead(id);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === id ? { ...notif, isRead: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Failed to mark notification as read:', err);
      throw err;
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, isRead: true }))
      );
      setUnreadCount(0);
    } catch (err: any) {
      console.error('Failed to mark all notifications as read:', err);
      throw err;
    }
  }, []);

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(async (id: string) => {
    try {
      await notificationApi.deleteNotification(id);
      setNotifications(prev => prev.filter(notif => notif.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
      // Update unread count if the deleted notification was unread
      const deletedNotif = notifications.find(n => n.id === id);
      if (deletedNotif && !deletedNotif.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      console.error('Failed to delete notification:', err);
      throw err;
    }
  }, [notifications]);

  /**
   * Refresh notifications (resets to first page) and unread count
   */
  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(), fetchUnreadCount()]);
  }, [fetchNotifications, fetchUnreadCount]);

  // Track how many notifications are currently loaded, for the poller
  useEffect(() => {
    loadedCountRef.current = notifications.length;
  }, [notifications]);

  // Initial fetch + poll every 30 seconds for new notifications
  useEffect(() => {
    refresh();

    const intervalId = setInterval(syncLatest, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [refresh, syncLatest]);

  return {
    notifications,
    total,
    unreadCount,
    loading,
    loadingMore,
    error,
    hasMore: notifications.length < total,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    syncLatest,
    loadMore,
  };
};
